import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, ArrowRight, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const REASON_STYLE = {
  'same email':        'bg-red-100 text-red-700',
  'same account code': 'bg-red-100 text-red-700',
  'same phone':        'bg-amber-100 text-amber-700',
  'similar name':      'bg-gray-100 text-gray-600',
};

// One side of a possible duplicate, with enough history to judge which to keep.
function Side({ c, onKeep, busy }) {
  return (
    <div className="flex-1 border rounded-lg p-3">
      <Link to={`/clients/${c.id}`} className="font-medium text-gray-800 hover:text-orange-600 hover:underline">
        {c.name}
      </Link>
      <div className="text-xs text-gray-500 mt-1 space-y-0.5">
        {c.account_code && <p className="font-mono">{c.account_code}</p>}
        {c.email && <p>{c.email}</p>}
        {c.phone && <p>{c.phone}</p>}
        <p className="text-gray-400">
          {c.invoices} invoice{c.invoices === 1 ? '' : 's'} · {c.parcels} parcel{c.parcels === 1 ? '' : 's'}
          {c.created_at ? ` · added ${new Date(c.created_at).toLocaleDateString('en-ZA')}` : ''}
        </p>
      </div>
      <button onClick={onKeep} disabled={busy}
        className="mt-2 w-full text-xs border border-gray-300 hover:border-orange-400 hover:bg-orange-50 rounded-lg py-1.5 disabled:opacity-50">
        Keep this one
      </button>
    </div>
  );
}

export default function DuplicateClients() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['client-duplicates'],
    queryFn: () => api.get('/clients/duplicates').then((r) => r.data),
  });

  // Preview first: show exactly what will move before anything is touched.
  const merge = async (keep, from) => {
    const key = keep.id + from.id;
    setBusy(key);
    try {
      const { data: p } = await api.post(`/clients/${keep.id}/merge`, { merge_id: from.id });
      const lines = Object.entries(p.moves || {}).map(([k, n]) => `  • ${n} × ${k.replace('.', ' → ')}`).join('\n');
      const ok = window.confirm(
        `Merge "${from.name}" into "${keep.name}"?\n\n` +
        (p.total ? `This moves:\n${lines}\n\n` : 'Nothing is linked to the duplicate.\n\n') +
        `"${from.name}" will then be deleted. This cannot be undone.`
      );
      if (!ok) return;
      await api.post(`/clients/${keep.id}/merge?commit=true`, { merge_id: from.id });
      toast.success(`Merged into ${keep.name}`);
      qc.invalidateQueries({ queryKey: ['client-duplicates'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Merge failed');
    } finally { setBusy(null); }
  };

  const pairs = data?.pairs || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Users size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Possible Duplicate Clients</h1>
          <p className="text-xs text-gray-400">Same person or business captured more than once</p>
        </div>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
        <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          Pairs are matched on a <strong>shared email, account code or phone number</strong>, or a
          <strong> closely similar name</strong>. Not every pair is a duplicate — related people at the
          same business will show up here too. Choose which record to keep; everything attached to the
          other (invoices, parcels, payments, statements) moves across before it is deleted.
        </p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">Loading…</div>
      ) : pairs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
          No possible duplicates found 🎉
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400 px-1">{pairs.length} pair{pairs.length === 1 ? '' : 's'} to review</p>
          <div className="space-y-3">
            {pairs.map((p, i) => {
              const key = p.a.id + p.b.id;
              return (
                <div key={i} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${REASON_STYLE[p.reason] || 'bg-gray-100 text-gray-600'}`}>
                      {p.reason}
                    </span>
                    {p.similarity != null && p.reason === 'similar name' && (
                      <span className="text-xs text-gray-400">{Math.round(p.similarity * 100)}% alike</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Side c={p.a} busy={busy === key} onKeep={() => merge(p.a, p.b)} />
                    <ArrowRight size={16} className="text-gray-300 flex-shrink-0" />
                    <Side c={p.b} busy={busy === key} onKeep={() => merge(p.b, p.a)} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
